/**
 * @fileoverview One convention for "which tab, with which filter" in the URL.
 *
 * A tile that opens a tab, a refresh and the Back button all have to agree,
 * so a page's tab state lives in `?tab=<id>&filter=<value>` rather than in a
 * `useState` that a reload forgets. Reads the params on every render and
 * writes them back on change; the page's default tab keeps a clean URL.
 */

import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * @param {Array<{id: string}|string>} tabs the page's tabs
 * @param {string} fallback the tab shown when the URL names none, or an unknown one
 * @returns {{tab: string, filter: string|null, setTab: (id: string, filter?: string|null) => void}}
 */
export function useTabParam(tabs, fallback) {
  const [params, setParams] = useSearchParams();
  const ids = tabs.map((t) => (typeof t === "string" ? t : t.id));
  const requested = params.get("tab");
  const tab = requested && ids.includes(requested) ? requested : fallback;
  const filter = params.get("filter") || null;

  const setTab = useCallback(
    (next, nextFilter = null) => {
      setParams((prev) => {
        const out = new URLSearchParams(prev);
        if (next === fallback && !nextFilter) out.delete("tab");
        else out.set("tab", next);
        if (nextFilter) out.set("filter", nextFilter);
        else out.delete("filter");
        return out;
      });
    },
    [setParams, fallback],
  );

  return { tab, filter, setTab };
}
